import { useMemo, useState } from 'react';

import { BubbleChatTranslateIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { LANGUAGE_MAP } from 'constants/countries';
import { FieldTypes } from 'constants/field-types-enum';
import { textWithReferences } from 'helpers/form';
import addValidators from 'helpers/validators/apply-validators';
import { useKeyboardHints } from 'hooks/keyboard-hints';
import { useLocale } from 'hooks/locale';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input';

import { FormMessage, Select, ValidationAlert } from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';
import ShakeMotion from 'components/Motion/ShakeMotion';
import StatusPill from 'components/Pills/StatusPills';

const normalizeContact = (contact, country) => {
  if (!contact) return '';
  const text = contact.toString();
  try {
    const parsed = parsePhoneNumber(text, country);
    if (parsed) return parsed.number;
  } catch (e) {
    // fall through to digits-only comparison
  }
  return text.replace(/[^\d+]/g, '');
};

const QuestionWrapper = ({
  previewMode = false,
  question,
  questionIndex,
  textParams = {},
  control,
  register,
  setValue,
  trigger,
  getValues,
  locale = 'en_GB',
  error,
  componentMap,
  checkValues,
  getComponentProps,
  pillSize = 'large',
  animationDisabled = false,
  requirementVariantStyle = 'pill',
  gap = 'large',
  titleSize = 'xx-large',
  fieldName,
  hideHeader = false,
  hideOptionalTag = false, // Hide the "Optional" tag (useful for support fields)
  isVisible = true,
  allValues,
  errors,
  rejectedData,
  sectionHeader, // { title, helpText }
  noMargin = false, // Suppress component-level margins for consistent spacing
  sectionHeaderSpacing = 'default', // 'default' | 'compact' | 'flush'
  allowTranslation = false,
  showLanguageSelector = true,
  selectedLanguageCode = null,
  translatedText = null,
  translatedDescription = null,
  translatedOptions = null,
  onLanguageChange = () => {},
  isTranslating = false,
  brand = {},
  enforceFieldValidation = true,
}) => {
  const showOptionalTag = !(hideOptionalTag || question?.hideOptionalTag);
  const { t } = useTranslation();
  const { dateHyphenFormat } = useLocale(locale);
  const keyboardHint = useKeyboardHints(question.type);

  const resolvedQuestion = useMemo(() => {
    if (!translatedOptions) return question;
    return { ...question, options: translatedOptions };
  }, [question, translatedOptions]);

  const QuestionComponent = componentMap[question.type];
  const name = fieldName || `${question.id}`;

  const deferHelpUntilChecked =
    !question?.showHelpAlways &&
    question?.showHelpWhenChecked &&
    (question?.type === FieldTypes.Checkbox || question?.type === FieldTypes.Consent);

  const helpWatchValue = useWatch({
    control,
    name,
    disabled: !deferHelpUntilChecked,
  });

  const showDeferredHelp =
    deferHelpUntilChecked &&
    (helpWatchValue === true || helpWatchValue === 'true' || helpWatchValue === 1);

  const {
    controlledInput = true,
    compareContacts = null,
    compareContactErrorMessage = '',
    ...componentProps
  } = useMemo(() => {
    const fromParent =
      getComponentProps.length !== 1
        ? getComponentProps(resolvedQuestion, undefined)
        : getComponentProps(undefined);
    if (fromParent && typeof fromParent === 'object' && 'controlledInput' in fromParent) {
      return fromParent;
    }
    const { getComponentProps: baseGetComponentProps } = require('helpers/question-config');
    return baseGetComponentProps({ question: resolvedQuestion, noMargin });
  }, [resolvedQuestion, noMargin, getComponentProps]);

  const shouldEnforceValidation =
    enforceFieldValidation && question?.enforceFieldValidation !== false;

  // Create comprehensive validation rules
  const baseValidationRules = isVisible
    ? addValidators(
        question.type,
        question.min,
        question.max,
        shouldEnforceValidation ? question.required : false,
        shouldEnforceValidation ? question.validatorName : null,
        question.validationParams,
        getValues,
        name,
      )
    : {};

  const validationRules = compareContacts
    ? {
        ...baseValidationRules,
        validate: {
          ...baseValidationRules.validate,
          compareContact: value => {
            const normalizedValue = normalizeContact(value, componentProps.defaultCountry);
            if (!normalizedValue) return true;
            return (
              !compareContacts.some(
                contact =>
                  normalizeContact(contact, componentProps.defaultCountry) === normalizedValue,
              ) || compareContactErrorMessage
            );
          },
        },
      }
    : baseValidationRules;

  const errorMessage = typeof error === 'string' ? error : error?.message;

  const noHeaderComponents = ['checkbox', 'consent', 'download', 'toggle_switch'];

  // Render section header if provided via prop
  const renderSectionHeader = () => {
    if (!sectionHeader) return null;

    // If only helpText exists (no title), render just the help text without section styling
    if (!sectionHeader.title && sectionHeader.helpText) {
      return (
        <div className="u-margin-top u-margin-bottom--small">
          <FormMessage
            message={textWithReferences(sectionHeader.helpText, textParams, { previewMode })}
            type="Info"
            noMargin
            closeable={false}
          />
        </div>
      );
    }

    // If title exists, render as a section header with margins
    // Extra margin-top for visual separation from previous section
    // No margin-bottom - gap will handle spacing, but we use negative margin to reduce it
    return (
      <div
        className={cn('d-flex flex-column u-margin-bottom--small', {
          'u-margin-top--small': sectionHeaderSpacing === 'compact',
          'u-margin-top--large': sectionHeaderSpacing === 'default',
        })}>
        {sectionHeader.title && (
          <span className="primary-text u-font-size-x-large u-font-weight-600">
            {textWithReferences(sectionHeader.title, textParams, { previewMode })}
          </span>
        )}
        {sectionHeader.helpText && (
          <div className="u-margin-top--small">
            <FormMessage
              message={textWithReferences(sectionHeader.helpText, textParams, { previewMode })}
              type="Info"
              noMargin
              closeable={false}
            />
          </div>
        )}
      </div>
    );
  };

  if (question?.type === 'statement') {
    return (
      <>
        {renderSectionHeader()}
        <div className="d-flex flex-column u-gap-small">
          {question?.title != null && question?.title !== '' && (
            <div
              className="html-embedded-lists primary-text u-font-size-large u-font-weight-500 white-space-pre-line"
              dangerouslySetInnerHTML={{
                __html: textWithReferences(question.title, textParams, { previewMode }) ?? '',
              }}
            />
          )}
          {question?.text != null && question.text !== '' && (
            <div
              className="html-embedded-lists secondary-text u-font-size-small u-font-weight-400 white-space-pre-line"
              dangerouslySetInnerHTML={{
                __html: textWithReferences(question.text, textParams, { previewMode }) ?? '',
              }}
            />
          )}
          {question?.helpText && (
            <FormMessage
              message={textWithReferences(question.helpText, textParams, { previewMode })}
              type="Info"
              noMargin
              closeable={false}
            />
          )}
        </div>
      </>
    );
  }

  if (question.type === 'object') {
    return null;
  }

  if (!QuestionComponent) {
    return <p>Unsupported question type: {question.type}</p>;
  }

  return (
    <>
      {renderSectionHeader()}
      <div
        className={` flex-column u-gap-${gap} u-width-100 ${question.type === 'hidden' ? 'd-hide' : 'd-flex'}`}>
        {!hideHeader && (
          <>
            {showOptionalTag && requirementVariantStyle === 'pill' && (
              <span className="secondary-text u-font-size-md u-font-weight-400 u-margin-bottom--small">
                <ShakeMotion shake={errorMessage} disabled={animationDisabled}>
                  <StatusPill
                    text={question.required ? t('common.required') : t('common.optional')}
                    {...(pillSize === 'small' ? { small: true } : { large: true })}
                    status={question.required ? 'warning' : 'neutral'}
                    show={questionIndex + 1}
                  />
                </ShakeMotion>
              </span>
            )}
            {!noHeaderComponents.includes(question.type) && (
              <div className="d-flex flex-column u-gap-small">
                <div className="d-flex flex-row u-gap-small align-items-center">
                  <div className="d-flex flex-column">
                    <span
                      className={` ${allowTranslation && !isTranslating && translatedText ? 'hidden' : `primary-text u-font-size-${titleSize} u-font-weight-500`}`}
                      dangerouslySetInnerHTML={{
                        __html: textWithReferences(question?.text || question?.title, textParams, {
                          previewMode,
                        }),
                      }}
                    />
                    {allowTranslation && (
                      <div className="d-flex flex-row flex-wrap u-gap-small align-items-center u-margin-top--small">
                        {isTranslating && (
                          <span className="text-tertiary u-font-size-x-small u-font-weight-400">
                            {t('components.form.translating')}
                          </span>
                        )}
                        {!isTranslating && translatedText && (
                          <span
                            className={`primary-text u-font-size-${titleSize} u-font-weight-500`}
                            dangerouslySetInnerHTML={{
                              __html:
                                textWithReferences(translatedText, textParams, { previewMode }) ??
                                '',
                            }}
                          />
                        )}
                        {!isTranslating && !translatedText && showLanguageSelector && (
                          <span className="text-tertiary u-font-size-x-small u-font-weight-300 u-font-style-italic">
                            {t('components.form.selectLanguagePlaceholder')}
                          </span>
                        )}
                        {!previewMode && showLanguageSelector && (
                          <Select
                            name={`${name}_language`}
                            placeholder="Select language"
                            value={selectedLanguageCode}
                            inLineSelect
                            selectIcon={BubbleChatTranslateIcon}
                            inputProps={{
                              onChange: e => {
                                onLanguageChange(e.target.value);
                              },
                            }}
                            useSearch>
                            {Object.entries(LANGUAGE_MAP).map(([code, label]) => (
                              <Select.Item key={code} value={code}>
                                {label}
                              </Select.Item>
                            ))}
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                  {showOptionalTag && requirementVariantStyle === 'text' && !question.required && (
                    <StatusPill text={'Optional'} status={'light'} />
                  )}
                </div>
                {(question?.use_description ?? true) && question?.description && (
                  <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: textWithReferences(
                          translatedDescription || question.description,
                          textParams,
                          {
                            previewMode,
                          },
                        ),
                      }}
                    />
                  </span>
                )}
              </div>
            )}
          </>
        )}
        <div className="d-flex flex-column u-gap-small" data-testid="question-answer">
          {controlledInput && !isVisible ? null : controlledInput ? (
            <Controller
              name={name}
              control={control}
              rules={validationRules}
              shouldUnregister={!isVisible}
              defaultValue={question?.defaultValue}
              render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => {
                const normalizedValue = (() => {
                  if (value !== undefined && value !== null) {
                    return value;
                  }
                  if (question?.defaultValue !== undefined && question?.defaultValue !== null) {
                    return question.defaultValue;
                  }
                  if (
                    question?.type === FieldTypes.Checkbox ||
                    question?.type === FieldTypes.Consent
                  ) {
                    return false;
                  }
                  if (
                    question?.type === FieldTypes.MultipleChoice &&
                    (question?.options?.multipleSelection || question?.options?.multiple_selection)
                  ) {
                    return [];
                  }
                  if (question?.type === FieldTypes.MultiSelect) {
                    return [];
                  }
                  if (question?.type === FieldTypes.MultiUpload) {
                    return [];
                  }
                  return '';
                })();

                const valueForComponent =
                  question?.type === FieldTypes.MultiUpload && !Array.isArray(normalizedValue)
                    ? []
                    : normalizedValue;
                const componentPropsFromGetter = getComponentProps(valueForComponent);
                // Coerce checked to boolean for Checkbox/Consent/ToggleSwitch (form/API may supply string e.g. "true"/"false")
                const checkedProp =
                  question?.type === FieldTypes.Checkbox ||
                  question?.type === FieldTypes.Consent ||
                  question?.type === FieldTypes.ToggleSwitch
                    ? normalizedValue === true ||
                      normalizedValue === 'true' ||
                      normalizedValue === 1
                    : normalizedValue;
                return (
                  <QuestionComponent
                    {...componentPropsFromGetter}
                    question={resolvedQuestion}
                    format={dateHyphenFormat}
                    value={
                      (question?.type === FieldTypes.MultiUpload ||
                        question?.type === FieldTypes.DocumentUpload) &&
                      componentPropsFromGetter.value != null
                        ? componentPropsFromGetter.value
                        : valueForComponent
                    }
                    textParams={textParams}
                    checked={checkedProp}
                    onChange={onChange}
                    error={fieldError}
                    setValue={setValue}
                    trigger={trigger}
                    componentMap={componentMap}
                    showKeyboardHelp
                    // Pass react-hook-form props for IdRequirementsWrapper
                    control={control}
                    getValues={getValues}
                    name={name}
                    allValues={allValues}
                    errors={errors}
                    rejectedData={rejectedData}
                    noMargin={true}
                    allowTranslation={allowTranslation}
                    brand={brand}
                  />
                );
              }}
            />
          ) : (
            <QuestionComponent
              {...componentProps}
              question={resolvedQuestion}
              componentMap={componentMap}
              format={dateHyphenFormat}
              register={register}
              control={control}
              setValue={setValue}
              trigger={trigger}
              name={name}
              validators={validationRules}
              showKeyboardHelp
              getComponentProps={getComponentProps}
              getValues={getValues}
              isVisible={isVisible}
              allValues={allValues}
              errors={errors}
              error={errorMessage}
              rejectedData={rejectedData}
              noMargin={noMargin}
              allowTranslation={allowTranslation}
              brand={brand}
            />
          )}
          {noHeaderComponents.includes(question.type) && (
            <div className="d-flex flex-column u-gap-small">
              <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                <span
                  dangerouslySetInnerHTML={{
                    __html: textWithReferences(
                      translatedDescription || question.description,
                      textParams,
                      { previewMode },
                    ),
                  }}
                />
              </span>
            </div>
          )}
          {keyboardHint}
        </div>
        {question?.helpText && (!deferHelpUntilChecked || showDeferredHelp) && (
          <div className="u-margin-top--small">
            <FormMessage message={question?.helpText} type="Info" noMargin closeable={false} />
          </div>
        )}
        {errorMessage && (
          <div
            className={`u-margin-top--small u-margin-bottom--small ${question?.type === 'dynamic_array' ? 'u-order-1' : ''}`}>
            <FadeInMotion key={name} disabled={animationDisabled}>
              <ValidationAlert show={true} message={errorMessage} />
            </FadeInMotion>
          </div>
        )}
      </div>
    </>
  );
};

export default QuestionWrapper;
